import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { BehaviorSubject, Observable, Subscription, catchError, combineLatest, distinctUntilChanged, finalize, map, of, shareReplay, startWith, switchMap, tap } from 'rxjs';
import { ToastPanelType } from 'src/app/config/app-constants';
import { ToastService } from 'src/app/shared/services/toast.service';
import { EdgeDeviceDto, EdgeDeviceService } from '../../services/edge-device.service';

@Component({
    selector: 'app-edge-device-select',
    templateUrl: './edge-device-select.component.html',
    styleUrls: ['./edge-device-select.component.scss'],
    standalone: false
})
export class EdgeDeviceSelectComponent implements OnInit, OnChanges, OnDestroy {
  /** The parent form this control is a part of. */
  @Input() form: FormGroup;
  /** Name of the form control to bind to on the parent form. */
  @Input() controlName: string;
  /** Determines whether the backing controls are enabled/disabled. */
  @Input() disabled: boolean;
  /** A deployment ID for edit flows to include already-bound devices. */
  @Input() currentDeploymentId?: string;
  /** IDs to exclude from options (seed device, other peer, etc). */
  @Input() excludeIds: string[] = [];
  /** Names of other edge-device peer controls (within the same form) to auto-exclude their selected values. */
  @Input() peerControlNames: string[] = [];
  /** Name of the seed (primary) edge device id control (defaults to 'edgeDeviceId'). */
  @Input() seedControlName: string = 'edgeDeviceId';

  /** Subjects for refetch context and filtering context. */
  private refetchCtx$ = new BehaviorSubject<{ currentDeploymentId?: string }>({});
  private filterCtx$ = new BehaviorSubject<{ excludeIds: string[] }>({ excludeIds: [] });

  /** Subscriptions */
  private optionsSub?: Subscription;
  private excludeSubs: Subscription[] = [];

  /** Observable collection of available edge devices (raw). */
  devices$!: Observable<EdgeDeviceDto[]>;
  /** Latest snapshot used for validation. */
  devices: EdgeDeviceDto[] = [];
  /** Filtered list to display. */
  filteredDevices$!: Observable<(EdgeDeviceDto & { label: string })[]>;
  /** Loading state for UX */
  loading = false;

  get selectControl(): FormControl<string> { return this.form.get(this.controlName) as FormControl<string>; }

  constructor(private api: EdgeDeviceService, private toast: ToastService) { }

  ngOnInit(): void {
  this.selectControl.addValidators(this.validateDevice());

    // load devices only when currentDeploymentId changes (excludeIds should not refetch)
    const currentDeploymentId$ = this.refetchCtx$.pipe(
      map(ctx => (ctx.currentDeploymentId || '') as string),
      distinctUntilChanged()
    );

    this.devices$ = currentDeploymentId$.pipe(
      tap(() => this.loading = true),
      switchMap(id => this.api.getAvailableEdgeDevices(id || undefined).pipe(
        catchError(err => {
          this.toast.openToast('Couldn\'t load devices. Retry.', ToastPanelType.error);
          return of([] as EdgeDeviceDto[]);
        }),
        finalize(() => this.loading = false)
      )),
      shareReplay(1)
    );

    const filterCtx$ = this.filterCtx$.pipe(
      map(x => (x.excludeIds || []).map(v => v?.toLowerCase()).sort().join('|')),
      distinctUntilChanged(),
      map(() => this.filterCtx$.value)
    );

    this.filteredDevices$ = combineLatest([
      this.selectControl.valueChanges.pipe(startWith(this.selectControl.value || '')),
      this.devices$,
      filterCtx$,
    ]).pipe(
      map(([value, devices, fctx]) => {
        const exclude = (fctx.excludeIds || []).map(x => x?.toLowerCase());
        const items = (devices || [])
          .filter(d => d?.edgeDeviceId)
          .filter(d => !exclude.includes(d.edgeDeviceId.toLowerCase()))
          .map(d => ({ ...d, label: d.edgeDeviceFriendlyName || d.edgeDeviceId }))
          .sort((a, b) => (a.label?.toLowerCase() ?? '').localeCompare(b.label?.toLowerCase() ?? '') || a.edgeDeviceId.localeCompare(b.edgeDeviceId));

        // Simple contains filter by id or label
        const query = (value || '').toLowerCase();
        return items.filter(d => d.edgeDeviceId.toLowerCase().includes(query) || (d.label || '').toLowerCase().includes(query));
      })
    );

    // Keep snapshot and retrigger validation when options refresh
    this.optionsSub = this.devices$.subscribe(list => {
      this.devices = list || [];
      this.selectControl.setValue(this.selectControl.value, { emitEvent: false });
    });

  // initialize contexts (deployment id + exclusion)
  this.emitContext();
  this.initAutoExcludeHandling();

    if (this.disabled) this.selectControl.disable();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['disabled']) {
      this.disabled ? this.selectControl.disable() : this.selectControl.enable();
    }
    const exclusionRelevant = ['excludeIds','peerControlNames','seedControlName','controlName','form'].some(k => !!changes[k]);
    if (exclusionRelevant) {
      this.reinitAutoExcludeHandling();
    }
    if (changes['currentDeploymentId']) {
      this.refetchCtx$.next({ currentDeploymentId: this.resolveCurrentDeploymentId() });
    }
  }

  ngOnDestroy(): void {
  this.optionsSub?.unsubscribe();
  this.excludeSubs.forEach(s => s.unsubscribe());
  }

  displayDevice = (id: string): string => {
    return this.devices?.find(d => d.edgeDeviceId === id)?.edgeDeviceFriendlyName || id;
  }

  private emitContext() {
    this.refetchCtx$.next({ currentDeploymentId: this.resolveCurrentDeploymentId() });
    this.updateExcludeContext();
  }

  /** Determine the effective deployment id if not explicitly passed in. */
  private resolveCurrentDeploymentId(): string | undefined {
    if (this.currentDeploymentId) return this.currentDeploymentId;
    // Attempt to infer from form hierarchy
    const direct = this.form?.get('currentDeploymentId')?.value || this.form?.get('deploymentId')?.value;
    if (direct) return direct;
    const parent = this.form?.parent as FormGroup | null;
    return parent?.get('currentDeploymentId')?.value || parent?.get('deploymentId')?.value || undefined;
  }

  /** Recompute exclude list (explicit takes precedence; otherwise derive from seed + peers). */
  private updateExcludeContext() {
    // Explicitly passed excludeIds wins
    if (this.excludeIds && this.excludeIds.length) {
      const uniqExplicit = Array.from(new Set(this.excludeIds.filter(Boolean)));
      this.filterCtx$.next({ excludeIds: uniqExplicit });
      return;
    }
    const auto = this.computeAutoExclude();
    this.filterCtx$.next({ excludeIds: auto });
  }

  /** Build an exclusion list from seed device + peer control values. */
  private computeAutoExclude(): string[] {
    const seedCtrl = this.form?.get(this.seedControlName) || this.form?.parent?.get(this.seedControlName);
    const seed = seedCtrl?.value;
    const peers = (this.peerControlNames || [])
      .filter(n => n && n !== this.controlName)
      .map(n => this.form?.get(n) || this.form?.parent?.get(n))
      .filter(c => !!c)
      .map(c => c!.value)
      .filter(v => !!v);
    return Array.from(new Set([seed, ...peers].filter(Boolean)));
  }

  /** Establish subscriptions to seed + peer control value changes for auto exclusion. */
  private initAutoExcludeHandling() {
    this.clearExcludeSubs();
    // Only set up auto handling if no explicit excludeIds were provided (else we honor explicit list)
    const shouldAuto = !this.excludeIds || this.excludeIds.length === 0;
    if (shouldAuto) {
      const seedCtrl = this.form?.get(this.seedControlName) || this.form?.parent?.get(this.seedControlName);
      if (seedCtrl) {
        this.excludeSubs.push(seedCtrl.valueChanges.subscribe(() => this.updateExcludeContext()));
      }
      (this.peerControlNames || []).forEach(n => {
        if (n === this.controlName) return;
        const ctrl = this.form?.get(n) || this.form?.parent?.get(n);
        if (ctrl) {
          this.excludeSubs.push(ctrl.valueChanges.subscribe(() => this.updateExcludeContext()));
        }
      });
    }
    // Initial compute
    this.updateExcludeContext();
  }

  private reinitAutoExcludeHandling() {
    this.initAutoExcludeHandling();
  }

  private clearExcludeSubs() {
    this.excludeSubs.forEach(s => s.unsubscribe());
    this.excludeSubs = [];
  }

  /** Custom validator: required handled externally; ensure value exists and not excluded. */
  private validateDevice(): ValidatorFn {
    return (control: AbstractControl<string, string>): ValidationErrors | null => {
      const val = control.value;
      if (!val) return null;
      if (!this.devices || this.devices.length === 0) return { NoOptionsError: true };
      if (!this.devices.find(d => d.edgeDeviceId === val)) return { InvalidOptionError: true };
      if ((this.excludeIds || []).some(id => id && id.toLowerCase() === val.toLowerCase())) return { InvalidOptionError: true };
      return null;
    };
  }
}
